import type { FirebaseOptions } from "firebase/app";

/**
 * Reads the Firebase web config from Vite env vars.
 *
 * Returns `null` when `VITE_FIREBASE_API_KEY` is missing or empty, which
 * signals `createAuthClient()` to fall back to `MockAuthClient` for
 * local dev and test runs. The presence of the API key is the single
 * source of truth — we intentionally do NOT gate on `import.meta.env.MODE`
 * or a separate provider flag, because those become a second source of
 * truth that can disagree with whether the Firebase config is actually
 * populated.
 *
 * Why `apiKey` specifically: Cloudflare Pages env vars are set as empty
 * strings (not undefined) for unconfigured variables in Terraform, and
 * `!apiKey` correctly falls through for both `""` and missing.
 */
export function readFirebaseConfig(): FirebaseOptions | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  if (!apiKey) return null;
  // Coerce empty strings to undefined for optional fields. Cloudflare
  // Pages sets unconfigured env vars to "" rather than omitting them,
  // and passing "" for an optional Firebase option makes the SDK log
  // a warning on initializeApp. undefined makes it treat the field as
  // absent, which is what we want when (for example) a Firebase Web
  // App hasn't been registered yet — email/password auth works
  // without appId.
  return {
    apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || undefined,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || undefined,
    appId: import.meta.env.VITE_FIREBASE_APP_ID || undefined,
  };
}
